// src/pages/billing/FinancialDashboardPage.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { invoiceService } from '../../services/invoice.service';
import { expenseService } from '../../services/expense.service';
import { incomeService } from '../../services/income.service';
import { userPaymentService } from '../../services/userPayment.service';
import { projectService } from '../../services/project.service';
import { Invoice, Expense, UserPayment } from '../../types/finance.types';
import { Project } from '../../types/project.types';
import { format, subMonths, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Users,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  PieChart,
  BarChart3,
  Calendar,
  AlertCircle,
  Check,
  Clock
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

interface FinancialSummary {
  // Revenue (Invoices)
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalOverdue: number;
  invoiceCount: number;

  // Expenses
  totalExpenses: number;
  expenseCount: number;

  // Staff Payments
  totalStaffPayments: number;
  pendingPayments: number;
  paymentCount: number;

  // Net
  netIncome: number;
  profitMargin: number;
}

interface MonthlyData {
  month: string;
  revenue: number;
  expenses: number;
  staffCosts: number;
  net: number;
}

interface ProjectFinancials {
  projectId: string;
  projectName: string;
  organizationName: string;
  status: string;
  revenue: number;
  expenses: number;
  staffCosts: number;
  netIncome: number;
  profitMargin: number;
  invoiceCount: number;
  expenseCount: number;
}

export default function FinancialDashboardPage() {
  const { userProfile } = useAuth();
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
  const [recentExpenses, setRecentExpenses] = useState<Expense[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectFinancials, setProjectFinancials] = useState<ProjectFinancials[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'revenue' | 'netIncome' | 'name'>('revenue');

  const canViewDashboard = userProfile?.role === 'provider_admin';

  useEffect(() => {
    loadDashboardData();
  }, [userProfile, selectedYear]);

  const loadDashboardData = async () => {
    if (!userProfile || !canViewDashboard) return;

    setLoading(true);
    try {
      const yearStart = startOfYear(new Date(selectedYear, 0, 1));
      const yearEnd = endOfYear(new Date(selectedYear, 0, 1));

      const [invoices, expenses, payments, projectsData, manualIncome] = await Promise.all([
        invoiceService.getInvoices(),
        expenseService.getExpenses({ startDate: yearStart, endDate: yearEnd }),
        userPaymentService.getUserPayments({ startDate: yearStart, endDate: yearEnd }),
        projectService.getAllProjects(),
        incomeService.getReceivedIncomeTotal({ startDate: yearStart, endDate: yearEnd })
      ]);

      setProjects(projectsData);
      setRecentInvoices(invoices.slice(0, 5));
      setRecentExpenses(expenses.slice(0, 5));

      // Calculate summary
      const yearInvoices = invoices.filter(inv => {
        const date = inv.issueDate.toDate();
        return date >= yearStart && date <= yearEnd;
      });

      const totalInvoiced = yearInvoices.reduce((sum, inv) => sum + inv.total, 0);
      // Revenue = paid invoices + received manual income (non-invoice money).
      const invoicePaid = yearInvoices
        .filter(inv => inv.status === 'paid')
        .reduce((sum, inv) => sum + inv.total, 0);
      const totalPaid = invoicePaid + (manualIncome || 0);
      const totalOutstanding = yearInvoices
        .filter(inv => inv.status === 'sent' || inv.status === 'overdue')
        .reduce((sum, inv) => sum + inv.total, 0);
      const totalOverdue = yearInvoices
        .filter(inv => inv.status === 'overdue')
        .reduce((sum, inv) => sum + inv.total, 0);

      const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);

      const paidPayments = payments.filter(p => p.status === 'paid');
      const pendingPayments = payments.filter(p => p.status === 'pending' || p.status === 'processing');
      const totalStaffPayments = paidPayments.reduce((sum, p) => sum + p.amount, 0);
      const totalPendingPayments = pendingPayments.reduce((sum, p) => sum + p.amount, 0);

      const netIncome = totalPaid - totalExpenses - totalStaffPayments;
      const profitMargin = totalPaid > 0 ? (netIncome / totalPaid) * 100 : 0;

      setSummary({
        totalInvoiced,
        totalPaid,
        totalOutstanding,
        totalOverdue,
        invoiceCount: yearInvoices.length,
        totalExpenses,
        expenseCount: expenses.length,
        totalStaffPayments,
        pendingPayments: totalPendingPayments,
        paymentCount: payments.length,
        netIncome,
        profitMargin
      });

      // Calculate monthly data
      const monthlyResults: MonthlyData[] = [];
      for (let month = 0; month < 12; month++) {
        const monthStart = startOfMonth(new Date(selectedYear, month, 1));
        const monthEnd = endOfMonth(new Date(selectedYear, month, 1));
        const monthName = format(monthStart, 'MMM');

        const monthRevenue = yearInvoices
          .filter(inv => {
            if (inv.status !== 'paid' || !inv.paidDate) return false;
            const paidDate = inv.paidDate.toDate();
            return paidDate >= monthStart && paidDate <= monthEnd;
          })
          .reduce((sum, inv) => sum + inv.total, 0);

        const monthExpenses = expenses
          .filter(exp => {
            const date = exp.date.toDate();
            return date >= monthStart && date <= monthEnd;
          })
          .reduce((sum, exp) => sum + exp.amount, 0);

        const monthStaffCosts = paidPayments
          .filter(p => {
            if (!p.paidDate) return false;
            const paidDate = p.paidDate.toDate();
            return paidDate >= monthStart && paidDate <= monthEnd;
          })
          .reduce((sum, p) => sum + p.amount, 0);

        monthlyResults.push({
          month: monthName,
          revenue: monthRevenue,
          expenses: monthExpenses,
          staffCosts: monthStaffCosts,
          net: monthRevenue - monthExpenses - monthStaffCosts
        });
      }
      setMonthlyData(monthlyResults);

      // Calculate project-by-project financials
      const projectFinancialsData: ProjectFinancials[] = projectsData.map(project => {
        // Get invoices for this project in the selected year
        const projectInvoices = yearInvoices.filter(inv => inv.projectId === project.id);
        const projectRevenue = projectInvoices
          .filter(inv => inv.status === 'paid')
          .reduce((sum, inv) => sum + inv.total, 0);

        // Get expenses for this project
        const projectExpenses = expenses.filter(exp => exp.projectId === project.id);
        const totalProjectExpenses = projectExpenses.reduce((sum, exp) => sum + exp.amount, 0);

        // Get staff payments for this project
        const projectPayments = paidPayments.filter(p => p.projectId === project.id);
        const totalProjectStaffCosts = projectPayments.reduce((sum, p) => sum + p.amount, 0);

        const netIncome = projectRevenue - totalProjectExpenses - totalProjectStaffCosts;
        const profitMargin = projectRevenue > 0 ? (netIncome / projectRevenue) * 100 : 0;

        return {
          projectId: project.id,
          projectName: project.name,
          organizationName: project.organizationId || 'N/A',
          status: project.status,
          revenue: projectRevenue,
          expenses: totalProjectExpenses,
          staffCosts: totalProjectStaffCosts,
          netIncome,
          profitMargin,
          invoiceCount: projectInvoices.length,
          expenseCount: projectExpenses.length
        };
      });

      // Filter to only show projects with any financial activity
      const activeProjectFinancials = projectFinancialsData.filter(
        pf => pf.revenue > 0 || pf.expenses > 0 || pf.staffCosts > 0
      );

      setProjectFinancials(activeProjectFinancials);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!canViewDashboard) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-gray-600 mt-2">You don't have permission to view the financial dashboard.</p>
        </div>
      </div>
    );
  }

  // Calculate max value for chart scaling
  const maxMonthlyValue = Math.max(
    ...monthlyData.map(m => Math.max(m.revenue, m.expenses + m.staffCosts))
  );

  // Years for dropdown
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Financial Dashboard</h1>
            <p className="mt-1 text-sm text-gray-600">
              Overview of revenue, expenses, and profitability
            </p>
          </div>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border rounded-md bg-white"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <LoadingSpinner size="lg" className="mx-auto" />
            <p className="mt-4 text-gray-600">Loading financial data...</p>
          </div>
        ) : summary && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* Revenue */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-600">${summary.totalPaid.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {summary.invoiceCount} invoices ({selectedYear})
                </div>
              </div>

              {/* Expenses */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600">${summary.totalExpenses.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-lg">
                    <TrendingDown className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {summary.expenseCount} expenses ({selectedYear})
                </div>
              </div>

              {/* Staff Costs */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Staff Payments</p>
                    <p className="text-2xl font-bold text-orange-600">${summary.totalStaffPayments.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <Users className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  ${summary.pendingPayments.toFixed(2)} pending
                </div>
              </div>

              {/* Net Income */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Net Income</p>
                    <p className={`text-2xl font-bold ${summary.netIncome >= 0 ? 'text-indigo-600' : 'text-red-600'}`}>
                      ${summary.netIncome.toFixed(2)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${summary.netIncome >= 0 ? 'bg-indigo-100' : 'bg-red-100'}`}>
                    <DollarSign className={`h-6 w-6 ${summary.netIncome >= 0 ? 'text-indigo-600' : 'text-red-600'}`} />
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {summary.profitMargin.toFixed(1)}% profit margin
                </div>
              </div>
            </div>

            {/* Accounts Receivable Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg mr-4">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Invoiced</p>
                    <p className="text-xl font-bold">${summary.totalInvoiced.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg mr-4">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Outstanding</p>
                    <p className="text-xl font-bold">${summary.totalOutstanding.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg mr-4">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Overdue</p>
                    <p className="text-xl font-bold text-red-600">${summary.totalOverdue.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly Chart */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h2 className="text-lg font-semibold mb-4">Monthly Overview</h2>
              <div className="h-64">
                <div className="flex h-full items-end justify-between gap-2">
                  {monthlyData.map((data, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div className="w-full flex flex-col gap-1" style={{ height: '200px' }}>
                        {/* Revenue bar */}
                        <div
                          className="w-full bg-green-500 rounded-t transition-all hover:bg-green-600"
                          style={{
                            height: maxMonthlyValue > 0 ? `${(data.revenue / maxMonthlyValue) * 100}%` : '0%',
                            minHeight: data.revenue > 0 ? '4px' : '0'
                          }}
                          title={`Revenue: $${data.revenue.toFixed(2)}`}
                        />
                        {/* Expenses bar */}
                        <div
                          className="w-full bg-red-500 rounded transition-all hover:bg-red-600"
                          style={{
                            height: maxMonthlyValue > 0 ? `${(data.expenses / maxMonthlyValue) * 100}%` : '0%',
                            minHeight: data.expenses > 0 ? '4px' : '0'
                          }}
                          title={`Expenses: $${data.expenses.toFixed(2)}`}
                        />
                        {/* Staff costs bar */}
                        <div
                          className="w-full bg-orange-500 rounded-b transition-all hover:bg-orange-600"
                          style={{
                            height: maxMonthlyValue > 0 ? `${(data.staffCosts / maxMonthlyValue) * 100}%` : '0%',
                            minHeight: data.staffCosts > 0 ? '4px' : '0'
                          }}
                          title={`Staff: $${data.staffCosts.toFixed(2)}`}
                        />
                      </div>
                      <span className="text-xs text-gray-500 mt-2">{data.month}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-center gap-6 mt-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                  <span className="text-sm text-gray-600">Revenue</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
                  <span className="text-sm text-gray-600">Expenses</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-orange-500 rounded mr-2"></div>
                  <span className="text-sm text-gray-600">Staff Costs</span>
                </div>
              </div>
            </div>

            {/* Project Breakdown */}
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="p-6 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold">Project Breakdown</h2>
                  <p className="text-sm text-gray-500">Financial performance by project ({selectedYear})</p>
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'revenue' | 'netIncome' | 'name')}
                  className="px-3 py-1.5 text-sm border rounded-lg bg-white"
                >
                  <option value="revenue">Sort by Revenue</option>
                  <option value="netIncome">Sort by Net Income</option>
                  <option value="name">Sort by Name</option>
                </select>
              </div>

              {projectFinancials.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <PieChart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No financial activity recorded for any projects in {selectedYear}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Project
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Revenue
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expenses
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Staff Costs
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Net Income
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Margin
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {[...projectFinancials]
                        .sort((a, b) => {
                          if (sortBy === 'revenue') return b.revenue - a.revenue;
                          if (sortBy === 'netIncome') return b.netIncome - a.netIncome;
                          return a.projectName.localeCompare(b.projectName);
                        })
                        .map((pf) => (
                          <tr key={pf.projectId} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-medium text-gray-900">{pf.projectName}</p>
                                <p className="text-xs text-gray-500">
                                  {pf.invoiceCount} invoice{pf.invoiceCount !== 1 ? 's' : ''} · {pf.expenseCount} expense{pf.expenseCount !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span className="text-green-600 font-medium">
                                ${pf.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span className="text-red-600">
                                ${pf.expenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span className="text-orange-600">
                                ${pf.staffCosts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span className={`font-semibold ${pf.netIncome >= 0 ? 'text-indigo-600' : 'text-red-600'}`}>
                                ${pf.netIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                pf.profitMargin >= 20 ? 'bg-green-100 text-green-800' :
                                pf.profitMargin >= 0 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {pf.profitMargin.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                    {/* Totals row */}
                    <tfoot className="bg-gray-50 font-semibold">
                      <tr>
                        <td className="px-6 py-4 text-gray-900">
                          Total ({projectFinancials.length} project{projectFinancials.length !== 1 ? 's' : ''})
                        </td>
                        <td className="px-6 py-4 text-right text-green-600">
                          ${projectFinancials.reduce((sum, pf) => sum + pf.revenue, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 text-right text-red-600">
                          ${projectFinancials.reduce((sum, pf) => sum + pf.expenses, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 text-right text-orange-600">
                          ${projectFinancials.reduce((sum, pf) => sum + pf.staffCosts, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 text-right text-indigo-600">
                          ${projectFinancials.reduce((sum, pf) => sum + pf.netIncome, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {(() => {
                            const totalRev = projectFinancials.reduce((sum, pf) => sum + pf.revenue, 0);
                            const totalNet = projectFinancials.reduce((sum, pf) => sum + pf.netIncome, 0);
                            const avgMargin = totalRev > 0 ? (totalNet / totalRev) * 100 : 0;
                            return (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                avgMargin >= 20 ? 'bg-green-100 text-green-800' :
                                avgMargin >= 0 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {avgMargin.toFixed(1)}%
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Invoices */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b flex justify-between items-center">
                  <h2 className="text-lg font-semibold">Recent Invoices</h2>
                  <Link to="/invoices" className="text-sm text-indigo-600 hover:text-indigo-700">
                    View All
                  </Link>
                </div>
                <div className="divide-y">
                  {recentInvoices.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">No invoices yet</div>
                  ) : (
                    recentInvoices.map(invoice => (
                      <div key={invoice.id} className="p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{invoice.invoiceNumber}</p>
                            <p className="text-sm text-gray-600">{invoice.clientName}</p>
                            <p className="text-xs text-gray-400">{invoice.projectName}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">${invoice.total.toFixed(2)}</p>
                            <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${getStatusColor(invoice.status)}`}>
                              {invoice.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Recent Expenses */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b flex justify-between items-center">
                  <h2 className="text-lg font-semibold">Recent Expenses</h2>
                  <Link to="/expenses" className="text-sm text-indigo-600 hover:text-indigo-700">
                    View All
                  </Link>
                </div>
                <div className="divide-y">
                  {recentExpenses.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">No expenses yet</div>
                  ) : (
                    recentExpenses.map(expense => (
                      <div key={expense.id} className="p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{expense.description}</p>
                            <p className="text-sm text-gray-600">{expense.categoryName}</p>
                            <p className="text-xs text-gray-400">
                              {expense.projectName || 'No project'} - {expense.userName}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-red-600">-${expense.amount.toFixed(2)}</p>
                            <p className="text-xs text-gray-400">
                              {format(expense.date.toDate(), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link
                to="/invoices"
                className="bg-white rounded-lg shadow p-4 hover:bg-gray-50 flex items-center"
              >
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Invoices</p>
                  <p className="text-xs text-gray-500">Create & manage</p>
                </div>
              </Link>

              <Link
                to="/expenses"
                className="bg-white rounded-lg shadow p-4 hover:bg-gray-50 flex items-center"
              >
                <div className="p-2 bg-red-100 rounded-lg mr-3">
                  <CreditCard className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium">Expenses</p>
                  <p className="text-xs text-gray-500">Track costs</p>
                </div>
              </Link>

              <Link
                to="/settings"
                className="bg-white rounded-lg shadow p-4 hover:bg-gray-50 flex items-center"
              >
                <div className="p-2 bg-gray-100 rounded-lg mr-3">
                  <DollarSign className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium">Settings</p>
                  <p className="text-xs text-gray-500">Company & banking</p>
                </div>
              </Link>

              <Link
                to="/reports"
                className="bg-white rounded-lg shadow p-4 hover:bg-gray-50 flex items-center"
              >
                <div className="p-2 bg-purple-100 rounded-lg mr-3">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium">Reports</p>
                  <p className="text-xs text-gray-500">Generate reports</p>
                </div>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
